import React from 'react';
import { Grid, Card, CardHeader, CardContent } from '@mui/material';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import { useClinicalData } from '../../hooks/useClinicalData';
import { useDermatologistActivity } from '../../hooks/useDermatologistActivity';
import { useCohortSummary } from '../../hooks/useCohortSummary';
import { useDiseaseBreakdown } from '../../hooks/useDiseaseBreakdown';
import { useLabConfirmationCoverage } from '../../hooks/useLabConfirmationCoverage';
import PatientsWithoutImages from '../../PatientsWithoutImages';
import ActivityPerDermatologist from '../../ActivityPerDermatologist';
import CohortSummaryCard from '../../CohortSummaryCard';
import DiseaseBreakdownByIcd11Root from '../../DiseaseBreakdownByIcd11Root';
import LabConfirmationCoverage from '../../LabConfirmationCoverage';
import OpenVisits from '../../OpenVisits';
import { useOpenVisits } from '../../hooks/useOpenVisits';
import { FUNCTIONALITY } from '../../../../../constants/types';
import { PERMISSION } from '../../../../../components/investigation/share/user_roles';

interface ClinicalTabProps {
    investigation: {
        uuid: string;
        permissions: string[];
        functionalities?: string[];
        [key: string]: any;
    };
    startDate?: number | null;
    endDate?: number | null;
    /** SKIN-432 — the viewer's language, injected by withLocalize below. */
    localeCode: string;
}

// SKIN-695 — open-visits resolve list. Its own component so the fetch only fires
// (and the card only mounts) for users who can resolve a visit.
const OpenVisitsSection: React.FC<{ investigation: { uuid: string } }> = ({ investigation }) => {
    const { data, loading, error, retry } = useOpenVisits(investigation);
    return (
        <Grid item xs={12}>
            <Card>
                <CardHeader title={<Translate id="hospital.analytics.clinical.open-visits.title" />} />
                <CardContent>
                    <OpenVisits loading={loading} error={error} rows={data} onRetry={retry} />
                </CardContent>
            </Card>
        </Grid>
    );
};

// SKIN-35 / SKIN-39 — "Clinical" analytics tab. Only rendered for investigations
// with FUNCTIONALITY.CLINICAL (gating handled in the tab config). Hosts activity
// per dermatologist (SKIN-39) and patients without images (SKIN-35).
const ClinicalTab: React.FC<ClinicalTabProps> = ({ investigation, startDate = null, endDate = null, localeCode }) => {
    const { data, loading, error, retry } = useClinicalData(investigation);
    const activity = useDermatologistActivity(investigation, startDate, endDate);
    const cohort = useCohortSummary(investigation, startDate, endDate);
    const disease = useDiseaseBreakdown(investigation, startDate, endDate, localeCode);
    const lab = useLabConfirmationCoverage(investigation, startDate, endDate);
    // SKIN-695 — the open-visits card is shown only to a user who can resolve a visit
    // (WEB_VISIT_STATUS study flag + APPROVE_VISIT), mirroring the dossier close/void gate.
    const canResolveVisits = Boolean(
        investigation?.functionalities?.includes(FUNCTIONALITY.WEB_VISIT_STATUS) &&
        investigation?.permissions?.includes(PERMISSION.APPROVE_VISIT),
    );

    return (
        <Grid container spacing={3}>
            {canResolveVisits && <OpenVisitsSection investigation={investigation} />}
            <Grid item xs={12}>
                <Card>
                    <CardHeader title={<Translate id="hospital.analytics.clinical.cohort-summary.title" />} />
                    <CardContent>
                        <CohortSummaryCard
                            loading={cohort.loading}
                            error={cohort.error}
                            data={cohort.data}
                            onRetry={cohort.retry}
                        />
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12}>
                <Card>
                    <CardHeader title={<Translate id="hospital.analytics.clinical.disease-breakdown.title" />} />
                    <CardContent>
                        <DiseaseBreakdownByIcd11Root
                            loading={disease.loading}
                            error={disease.error}
                            rows={disease.rows}
                            coverage={disease.coverage}
                            onRetry={disease.retry}
                        />
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12}>
                <Card>
                    <CardHeader title={<Translate id="hospital.analytics.clinical.lab-confirmation.title" />} />
                    <CardContent>
                        <LabConfirmationCoverage
                            loading={lab.loading}
                            error={lab.error}
                            data={lab.data}
                            onRetry={lab.retry}
                        />
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12}>
                <Card>
                    <CardHeader title={<Translate id="hospital.analytics.clinical.activity-per-dermatologist.title" />} />
                    <CardContent>
                        <ActivityPerDermatologist loading={activity.loading} error={activity.error} rows={activity.data} onRetry={activity.retry} />
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12}>
                <Card>
                    <CardHeader title={<Translate id="hospital.analytics.clinical.patients-without-images.title" />} />
                    <CardContent>
                        <PatientsWithoutImages loading={loading} error={error} rows={data} onRetry={retry} />
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

/**
 * `withLocalize` wraps the tab, and the tab passes the code down — the shape
 * VisitDetail records for this exact situation, and the one the rest of the repo
 * uses. `activeLanguage.code` with no fallback on purpose: a `?? 'en'` here
 * would silently pick a language, and an undefined active language is not a
 * state this page can be in without every <Translate> on it being broken too.
 */
const ClinicalTabLocalized = ({ activeLanguage, ...rest }: LocalizeContextProps & Omit<ClinicalTabProps, 'localeCode'>) => (
    <ClinicalTab {...rest} localeCode={activeLanguage.code} />
);

export default withLocalize(ClinicalTabLocalized);
